import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Beaker, Menu, X, BookOpen, ArrowRight, Bot } from 'lucide-react';
import { MarkdownRenderer } from '@/components/chat/MarkdownRenderer';

const WHITEPAPER_MD = `## Executive Summary

Pharmacometrics is the quantitative backbone of drug development. The discipline integrates pharmacokinetic, pharmacodynamic, and disease models to optimize dosing, predict outcomes, and support regulatory decisions. However, the current analytical workflow requires pharmacometricians to manually orchestrate multiple specialized software applications, each serving a single purpose in a multi-step pipeline.

**PharmAgent** is an web-based, multi-agent AI platform designed to integrate the complete pharmacometric workflow within a single conversational interface. Built on a hierarchical architecture of **13 specialized AI agents** controlling **151 deterministic computational tools**, PharmAgent automates routine analytical steps while preserving human scientific judgment at critical decision points through configurable review gates.

This white paper describes the platform's architecture, capabilities, and implications for the field.

---

## 1. Background: The Current Analytical Workflow

A standard model-informed drug development (MIDD) analysis involves several sequential steps, each typically requiring different software tools:

| Step | Commonly Used Tools | Typical Duration |
|---|---|---|
| Data assembly (SDTM to analysis dataset) | SAS, R, Python | 1-2 weeks |
| Exploratory data analysis and NCA | R (PKNCA), Phoenix WinNonlin | 2-4 days |
| Population PK/PD modeling | NONMEM, Monolix, nlmixr2 | 2-4 weeks |
| Diagnostics (GOF, VPC, bootstrap) | PsN, xpose, vpc (R packages) | 3-5 days |
| Simulation and dose optimization | mrgsolve, SimulX, PopED | 2-3 days |
| Report writing and TFL compilation | Microsoft Word, R Markdown | 1-2 weeks |

**Typical timeline: 4-8 weeks per analysis across 5-10 software tools.**

The pharmacometrician currently serves as the integration layer between these tools, manually transferring data, results, and visualizations between applications. No tool shares state with another, and each requires its own data format and interface conventions.

### 1.1 Recent Advances in the Field

Several research groups and organizations have begun exploring AI-assisted pharmacometrics:

- **Pfizer (Apollo-AI, CTS {new Date().getFullYear()})** published a conceptual framework for agentic AI in quantitative clinical pharmacology
- **AstraZeneca (pyDarwin)** developed automated PopPK model building using Bayesian optimization
- **PumasAI** created hybrid neural ODE modeling (DeepPumas) and embedded AI assistants
- **University at Buffalo (PEARL, CPT:PSP {new Date().getFullYear()})** built a RAG system for regulatory compliance evaluation
- **QSP-Copilot (CPT:PSP {new Date().getFullYear()})** demonstrated the first multi-agent system for QSP modeling

These contributions address individual components of the workflow. PharmAgent aims to integrate them into a unified platform.

---

## 2. PharmAgent: Platform Design

### 2.1 Hierarchical Agent Architecture

PharmAgent employs a three-level agent hierarchy organized by analytical method:

**Level 0 - Orchestration:**
- Supervisor Agent: Classifies user intent, routes to specialists, executes workflow templates, manages error recovery

**Level 1 - Domain Agents (10):**
- Data Manager (14 tools): Dataset profiling, CDISC validation, format conversion
- NCA Agent (12 tools): Non-compartmental analysis with linear-up/log-down trapezoidal rule
- Modeler Manager (5 tools): Routes to Level 2 modeling specialists
- PBPK Agent (15 tools): Whole-body PBPK, DDI prediction
- Statistical Agent (16 tools): XGBoost, SHAP, Cox PH, causal inference
- Simulator Agent (14 tools): Monte Carlo, virtual populations, target attainment
- QC Agent (12 tools): 15-point diagnostic checklist
- Report Agent (10 tools): Regulatory document generation
- Regulatory Intel Agent (9 tools): RAG search over guidances

**Level 2 - Modeling Specialists (3):**
- PopPK Expert (18 tools): Structural model selection, covariate analysis
- PKPD Expert (14 tools): Emax, indirect response, disease progression
- E-R Expert (12 tools): Logistic, survival, dose justification

**Total: 13 agents, 151 tools, 76 workflow templates.**

### 2.2 Core Design Principles

**Agents decide, tools execute.** Large language models reason about analytical strategy. Deterministic tools (scipy, numpy, XGBoost) perform computation. This separation ensures reproducible results while leveraging AI for workflow orchestration.

**Schema-only privacy.** A SchemaExtractor service processes every dataset before any LLM interaction, producing metadata-only summaries: column names, data types, subject counts, dose levels, and aggregate statistics. Individual patient data, identifiers, and raw concentration values are never transmitted to the AI model.

**Human-in-the-loop.** Workflow templates include configurable review gates at scientific decision points: base model selection, covariate model finalization, final model designation, and report approval. The platform supports the analyst's workflow; it does not replace scientific judgment.

**Hash-chain audit trail.** Every tool invocation is recorded with SHA-256 hashed inputs and outputs, linked in a cryptographic chain. Modification of any intermediate entry invalidates subsequent hashes, providing verifiable computational traceability.

---

## 3. Analytical Capabilities

| Domain | Implemented Methods |
|---|---|
| NCA | AUC (linear-up/log-down), Cmax, Tmax, t1/2, lambda-z (with R-squared quality), bioequivalence (TOST, 90% CI), dose proportionality (power model) |
| Population PK | 1/2/3-compartment models, transit absorption, TMDD, allometric scaling, SHAP + stepwise covariate analysis |
| PK/PD | Emax, sigmoid Emax, indirect response (Jusko I-IV), transit signal, tumor growth inhibition, Friberg neutropenia |
| Exposure-Response | Logistic regression (binary), Emax (continuous), parametric survival, dose justification |
| PBPK | Whole-body model (15 organs), ACAT absorption, CYP/UGT metabolism, mechanistic DDI, pediatric extrapolation |
| Statistical | XGBoost + SHAP feature importance, Cox proportional hazards, Kaplan-Meier, covariate screening |
| Simulation | Monte Carlo (lognormal PK), virtual population generation, target attainment, optimal design |
| Quality Control | 15-point checklist: GOF (6-panel), VPC, bootstrap (with histograms), convergence, shrinkage, condition number, parameter plausibility |
| Reporting | Multi-section regulatory documents (DOCX), NCA summary tables, labeling language (Section 12.3), ICH M15 packages |
| Regulatory Intelligence | Vector search (Qdrant) over 24 FDA/EMA/ICH/PMDA guidances, requirements checklists, agency comparison |

### 3.1 Workflow Templates

76 parameterized workflow templates organized across 11 categories: NCA (6), PopPK (6), PKPD (7), E-R (7), Special Populations (9), DDI (5), Simulation (7), Precision Dosing (3), PBPK (7), Reports (10), Study Design (9).

Each template specifies: ordered steps with agent assignments, configurable parameters with defaults, error handling policies, human review gates, and conditional execution logic.

---

## 4. Technology

| Component | Implementation |
|---|---|
| Agent orchestration | Custom StateGraph with PharmState shared bus |
| LLM | Anthropic Claude (Sonnet for routing, Opus for complex reasoning) |
| Scientific computing | scipy, numpy, pandas, XGBoost, scikit-learn, SHAP |
| Visualization | Plotly.js (15+ diagnostic chart types) |
| Document export | python-docx (DOCX), kaleido (PNG) |
| Backend | FastAPI, MongoDB, Python 3.11 |
| Frontend | React 18, Tailwind CSS, Shadcn/UI |
| Vector search | Qdrant (regulatory guidance RAG) |

---

## 5. Implications and Future Directions

**For the research community:** PharmAgent provides an community reference implementation for AI-augmented pharmacometric analysis. The modular agent-tool-template architecture enables community contributions and benchmarking.

**For regulatory science:** With ICH M15 (General Principles for Model-Informed Drug Development) becoming effective July {new Date().getFullYear()}, platforms that natively generate M15-compliant documentation with computational audit trails are well-positioned to support evolving regulatory expectations.

**For clinical practice:** The template system enables standardization of analytical workflows, reducing variability across analysts while maintaining flexibility for project-specific customization.

**Future work** includes integration of additional computational engines (NONMEM, Monolix via containerized execution), expansion of the regulatory guidance database, and development of collaborative multi-user workflows.

---

*PharmAgent: An Agentic AI Platform for End-to-End Pharmacometric Analysis*
*White Paper v1.0 | February {new Date().getFullYear()}*
*Malek Okour, PhD*
`;

export default function WhitepaperPage() {
  const [mobileMenu, setMobileMenu] = useState(false);
  const navigate = useNavigate();
  const links = [
    { to: '/features', label: 'Features' }, { to: '/about', label: 'About' },
    { to: '/architecture', label: 'Architecture' }, { to: '/whitepaper', label: 'Whitepaper' },
    { to: '/blog', label: 'Blog' },
  ];

  return (
    <div className="">

      <section className="pt-16 sm:pt-20 pb-8 px-4 sm:px-6">
        <div className="max-w-[800px] mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 mb-4">
              <BookOpen className="w-3 h-3" /> White Paper v1.0
            </span>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight" style={{ fontFamily: 'Space Grotesk' }}>
              PharmAgent: An Agentic AI Platform for End-to-End Pharmacometric Analysis
            </h1>
            <div className="flex flex-wrap items-center gap-3 mt-4 text-sm text-muted-foreground">
              <span>Malek Okour, PhD</span>
              <span className="text-muted-foreground/40">&bull;</span>
              <span>February {new Date().getFullYear()}</span>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="pb-16 px-4 sm:px-6">
        <div className="max-w-[800px] mx-auto">
          <MarkdownRenderer content={WHITEPAPER_MD} />
        </div>
      </section>

      <section className="py-8 px-4 sm:px-6 border-t border-border/30">
        <div className="max-w-[500px] mx-auto text-center">
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => navigate('/architecture')} variant="outline" className="rounded-full h-10 px-5 text-sm">
              <Bot className="w-4 h-4 mr-1.5" /> View Architecture
            </Button>
            <Button onClick={() => navigate('/signup')} className="rounded-full h-10 px-5 text-sm">
              Try Platform <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </section>

    </div>
  );
}
